import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CustomHtmlSlot from '@/common/components/CustomHtmlSlot';
import reCaptcha from '@/utils/recaptcha2';
import {turnstileWidgetRender, turnstileWidgetRemove} from '@/utils/turnstile';
import DotLoader from '../dot-loader';

const { Customer } = Mobi2Go;

const AddGiftCardForm = ({
    onAddSuccess,
    SubmitLayout,
    classNames: {
        fields,
        instructionsContainer,
        instructions,
        inputContainer,
        input,
        errorsContainer,
        errors,
        button,
    },
}) => {
    const turnstile_widget_id = 'cloudflare-turnstile-addgiftcardform';

    const [cardNumber, setCardNumber] = useState('');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [t] = useTranslation('gift-card');
    const [serverErrorTranslation] = useTranslation('server-errors');
    const [isLoading, setIsLoading] = useState(false);

    const credit_program = Mobi2Go.headoffice?.credit_program?.scheme_id?.toLowerCase() || '';

    const fetchTurnstileResponse = async widget_id => {
        const turnstile_config = Mobi2Go.app.feature_flags?.cloudflare_turnstile || {};
        const loyalty_program_with_gift_cards = Customer.getEnabledLoyaltyProgramWithGiftCardsEnabled();
        const turnstile_enabled = !!turnstile_config.site_key && (!!turnstile_config[credit_program] || !!turnstile_config[loyalty_program_with_gift_cards.toLowerCase()]);

        if (!turnstile_enabled) {
            return '';
        }

        try {
            return await turnstileWidgetRender(
                turnstile_config.site_key,
                widget_id
            );
        } finally {
            turnstileWidgetRemove(widget_id);
        }
    };

    const fetchReCaptchaResponse = async () => {
        const recaptcha_config = Mobi2Go.app.feature_flags?.google_recaptcha || {};
        const loyalty_program_with_gift_cards = Customer.getEnabledLoyaltyProgramWithGiftCardsEnabled();
        const recaptcha_enabled = !!recaptcha_config.site_key && (!!recaptcha_config[credit_program] || !!recaptcha_config[loyalty_program_with_gift_cards.toLowerCase()]);

        if (!recaptcha_enabled) {
            return '';
        }

        return await reCaptcha(recaptcha_config.site_key);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            setIsLoading(true);

            await Customer.addGiftCardAsync({
                card_number: cardNumber,
                pin,
                recaptcha_response: await fetchReCaptchaResponse(),
                turnstile_response: await fetchTurnstileResponse(turnstile_widget_id)
            });

            Customer.refresh();
            setCardNumber('');
            setPin('');
            setError('');
            onAddSuccess();
        } catch (error) {
            switch (error.status) {
                case 400:
                    setError(`Error: ${t('add_card_error')}`);
                    return true;

                case 429:
                    setError(`Error: ${t('too_many_requests_error')}`);
                    return true;

                case 0:
                    setError(`Error: ${serverErrorTranslation('timeout_error_body')}`);
                    return true;

                default:
                    Mobi2Go.trigger(
                        'app',
                        'serverRequestError',
                        error.xhr,
                        error.error,
                        undefined,
                        false
                    );

                    throw error;
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <form className="m2g-add-gift-card-form" onSubmit={handleSubmit}>
                <div className={fields}>
                    <div className={instructionsContainer}>
                        <div className={instructions}>
                            {t('add_instructions')}
                        </div>
                    </div>
                    <div className={inputContainer}>
                        <input
                            className={input}
                            type="text"
                            name="cardNumber"
                            required
                            pattern="[0-9]{9,21}"
                            placeholder="xxxx xxxx xxxx xxxx"
                            value={cardNumber}
                            onChange={({ target: { value } }) =>
                                setCardNumber(value)
                            }
                        />
                    </div>
                    <div className={inputContainer}>
                        <input
                            className={input}
                            type="text"
                            name="pin"
                            pattern="[0-9]{3,8}"
                            placeholder="xxx"
                            value={pin}
                            onChange={({ target: { value } }) => setPin(value)}
                        />
                    </div>
                    {error && (
                        <div className={errorsContainer}>
                            <div className={errors}>{error}</div>
                        </div>
                    )}
                    <div id={turnstile_widget_id}></div>
                </div>
                <SubmitLayout>
                    <button
                        type="submit"
                        className={`m2g-button ${button}`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <DotLoader dotCharacter="." />
                        ) : (
                            t('label_add_card')
                        )}
                    </button>
                </SubmitLayout>
            </form>

            <CustomHtmlSlot slot="gift-card" />
        </>
    );
};

AddGiftCardForm.defaultProps = {
    SubmitLayout: ({ children }) => children,
    classNames: {},
};

AddGiftCardForm.propTypes = {
    onAddSuccess: PropTypes.func.isRequired,
    SubmitLayout: PropTypes.func,
    classNames: PropTypes.shape({
        fields: PropTypes.string,
        instructionsContainer: PropTypes.string,
        instructions: PropTypes.string,
        inputContainer: PropTypes.string,
        input: PropTypes.string,
        errorsContainer: PropTypes.string,
        errors: PropTypes.string,
        button: PropTypes.string,
    }),
};

export default AddGiftCardForm;
